import { sipExtensionProvisioningSchema, sipExtensionOwnStatusSchema, type SipExtensionProvisioningDto, type SipExtensionOwnStatusDto, type SipExtensionReadDto } from '@easyflow/shared';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { browserLifecycle } from '@easyflow/ui-frontend';
import { sipPhoneService, CallState, CallInfo, SipPhoneConfig } from '@/lib/sipPhone';
import { apiClient } from '@/lib/api';
import { loadOwnSipPhoneConfig } from '@/lib/sipProvisioning';
import { toast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { useSocketOptional } from './SocketContext';
import { getErrorMessage } from '../lib/utils';


interface RecognizedCustomer {
  type: 'b2c' | 'b2b' | 'b2b_contact' | 'lead';
  id: string;
  name: string;
  phone?: string;
  email?: string;
  company_name?: string;
  role?: string;
  customer_id?: string;
  lead_stage?: string;
  lead_stage_color?: string;
}

export interface SupervisedSession {
  mode: 'listen' | 'whisper' | 'barge';
  supervisorExtension: string | null;
  startedAt: number;
}

interface SipPhoneContextType {
  state: CallState;
  callInfo: CallInfo | null;
  /** Task #1336 — popolato quando un supervisor ha avviato 880/881/882 sull'interno utente. */
  supervisedSession: SupervisedSession | null;
  isRegistered: boolean;
  isInCall: boolean;
  fsIsRegistered: boolean;
  fsConnectionType: string | null;
  // CRM Customer Recognition
  recognizedCustomer: RecognizedCustomer | null;
  // UI State - Persistent phone
  isOpen: boolean;
  isMinimized: boolean;
  activeExtension: string | null;
  // Pending dial number (set by sip:dial_number event for auto_dial=false)
  pendingDialNumber: string | null;
  clearPendingDialNumber: () => void;
  // Permissions
  canControlAllExtensions: boolean;
  // Actions
  connect: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  ensureRegistered: () => Promise<boolean>;
  switchExtension: (extension: string) => Promise<boolean>;
  openPhone: () => void;
  closePhone: () => void;
  minimizePhone: () => void;
  maximizePhone: () => void;
  call: (destination: string) => Promise<boolean>;
  answer: () => Promise<boolean>;
  hangup: () => Promise<boolean>;
  hold: () => Promise<boolean>;
  unhold: () => Promise<boolean>;
  mute: () => boolean;
  unmute: () => boolean;
  transfer: (destination: string) => Promise<boolean>;
  sendDTMF: (tone: string) => boolean;
  toggleHold: () => Promise<boolean>;
  toggleMute: () => boolean;
  refreshFsStatus: () => Promise<void>;
}

const SipPhoneContext = createContext<SipPhoneContextType | null>(null);

export function SipPhoneProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const sharedSocket = useSocketOptional();
  // Destructure for stable references and proper dependency tracking
  const socketIsConnected = sharedSocket?.isConnected ?? false;
  const socketVersion = sharedSocket?.socketVersion ?? 0;
  const socketOn = sharedSocket?.on;
  const socketOff = sharedSocket?.off;
  const socketEmit = sharedSocket?.emit;
  
  const [state, setState] = useState<CallState>('idle');
  const [callInfo, setCallInfo] = useState<CallInfo | null>(null);
  const [fsIsRegistered, setFsIsRegistered] = useState<boolean>(false);
  const [fsConnectionType, setFsConnectionType] = useState<string | null>(null);
  const [recognizedCustomer, setRecognizedCustomer] = useState<RecognizedCustomer | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeExtension, setActiveExtension] = useState<string | null>(null);
  const [pendingDialNumber, setPendingDialNumber] = useState<string | null>(null);
  const [supervisedSession, setSupervisedSession] = useState<SupervisedSession | null>(null);
  
  const isConnectedRef = useRef(false);
  const registrationPromiseRef = useRef<Promise<boolean> | null>(null);
  const autoRegisterDoneRef = useRef(false);
  const userExtension = user?.extension;
  const userRole = user?.role;
  
  // Permission: admin/super_admin can control all extensions
  const canControlAllExtensions = userRole === 'super_admin' || userRole === 'admin';

  const lastFsStatusFetchRef = useRef<number>(0);
  const FS_STATUS_COOLDOWN = 10000; // 10 seconds cooldown between fetches

  const refreshFsStatus = useCallback(async () => {
    if (!userExtension) return;
    
    // Debounce: skip if fetched recently
    const now = Date.now();
    if (now - lastFsStatusFetchRef.current < FS_STATUS_COOLDOWN) {
      return;
    }
    lastFsStatusFetchRef.current = now;
    
    try {
      // Self-scoped metadata: no secret delivery or PBX-management permission.
      const response = await apiClient.get<SipExtensionOwnStatusDto | null>('/voip/my-extension/status');
      if (response?.success && response?.data) {
        const extData = sipExtensionOwnStatusSchema.parse(response.data);
        setFsIsRegistered(extData.is_registered || false);
        setFsConnectionType(extData.connection_type || null);
      }
    } catch (error) {
      // Silently handle 429 errors
      if ((error as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message?.includes('429') || (error as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message?.includes('rate limit')) {
        return;
      }
      console.error('[SipPhoneContext] Error fetching FS status:', error);
    }
  }, [userExtension]);

  const wsFailureReportedRef = useRef(false);

  useEffect(() => {
    sipPhoneService.setEventCallback((newState: CallState, info?: CallInfo) => {
      setState(newState);
      setCallInfo(info || null);

      // Task #3158 — un reject del server sul REGISTER (403/404/5xx) non e`
      // piu` fatale: il softphone ritenta da solo con backoff lento. Il report
      // di sicurezza resta (una volta per finestra di reject) per osservabilita`.
      const rejectCode = sipPhoneService.registerRejectCode;
      if ((newState === 'error' || newState === 'unregistered') && rejectCode !== null && !wsFailureReportedRef.current && isAuthenticated) {
        wsFailureReportedRef.current = true;
        apiClient.post('/security/ws-failure', {
          extension: activeExtension || userExtension || null,
          error: `WebSocket SIP registration rejected (${rejectCode}), retrying with backoff`
        }).catch((err: unknown) => {
          console.warn('[SipPhoneContext] Failed to report WS failure:', err);
        });
      }

      if (newState === 'registered' || newState === 'connected') {
        wsFailureReportedRef.current = false;
      }

      // Reload-race hardening: se il connect() iniziale ritorna false ma il
      // retry interno del servizio recupera da solo (o viceversa il servizio
      // perde la registrazione), isConnectedRef derivava solo dal ritorno di
      // connect() → drift: un successivo ensureRegistered avrebbe smontato
      // una registrazione sana. Sincronizza sugli eventi del servizio.
      if (newState === 'registered') {
        isConnectedRef.current = true;
      } else if (newState === 'unregistered' || newState === 'error') {
        isConnectedRef.current = false;
      }
    });

    return () => {
      sipPhoneService.setEventCallback(() => {});
    };
  }, [isAuthenticated, activeExtension, userExtension]);

  // Socket for SIP status updates (requires extension)
  useEffect(() => {
    if (!userExtension) return;
    refreshFsStatus();
  }, [userExtension, refreshFsStatus]);

  // Task #2569 — al ritorno-in-primo-piano / online ri-sincronizza il badge
  // con la verita` backend: dopo sleep/wake o cambio rete lo stato locale puo`
  // essere stantio mentre il softphone si ri-registra in background.
  useEffect(() => {
    if (!userExtension) return;
    return browserLifecycle.subscribe((event) => {
      if (event.type === 'offline') return;
      refreshFsStatus();
    });
  }, [userExtension, refreshFsStatus]);

  // Use shared socket for real-time updates including CRM customer recognition
  // socketVersion ensures re-binding after socket reconnection
  useEffect(() => {
    console.log('[SipPhoneContext] Socket effect running:', { socketIsConnected, socketVersion, hasOn: !!socketOn, hasOff: !!socketOff });
    
    if (!socketIsConnected || !socketOn || !socketOff) {
      console.log('[SipPhoneContext] Shared socket not ready, waiting...', { socketIsConnected, hasOn: !!socketOn });
      return;
    }

    console.log('[SipPhoneContext] Binding socket events (version:', socketVersion, ')');

    const handleExtensionStatusChange = (data: { extension?: string; is_registered?: boolean; connection_type?: string | null }) => {
      if (userExtension && data.extension === userExtension) {
        setFsIsRegistered(data.is_registered || false);
        setFsConnectionType(data.connection_type || null);
      }
    };

    const handleExtensionsHealth = () => {
      if (userExtension) {
        refreshFsStatus();
      }
    };

    const handleCustomerIdentified = (event: { event_type?: string; direction?: string; customer?: RecognizedCustomer }) => {
      console.log('[SipPhoneContext] crm:customer_identified received:', event);
      if (event.event_type === 'call' && event.direction === 'inbound' && event.customer) {
        console.log('[SipPhoneContext] Setting recognized customer:', event.customer);
        setRecognizedCustomer(event.customer);
      }
    };

    const handleSipDialNumber = async (data: { requestId?: string; destination: string; extension: string; auto_dial: boolean }) => {
      console.log('[SipPhoneContext] sip:dial_number received:', data);
      const currentExt = activeExtension || userExtension;

      if (!isConnectedRef.current) {
        console.warn('[SipPhoneContext] sip:dial_number: SIP phone not registered');
        toast({
          title: 'Softphone non attivo',
          description: 'Il softphone non è registrato nel browser. Aprire il telefono e verificare la registrazione SIP.',
          variant: 'destructive'
        });
        if (socketEmit) {
          socketEmit('sip:dial_result', {
            requestId: data.requestId,
            extension: data.extension,
            success: false,
            error: 'Softphone non registrato nel browser',
            reason: 'not_registered'
          });
        }
        return;
      }

      if (data.extension && currentExt && data.extension !== currentExt) {
        console.warn('[SipPhoneContext] sip:dial_number: extension mismatch', { expected: data.extension, current: currentExt });
        toast({
          title: 'Estensione non corrispondente',
          description: `L'estensione ${data.extension} richiesta non corrisponde a quella attiva (${currentExt}).`,
          variant: 'destructive'
        });
        if (socketEmit) {
          socketEmit('sip:dial_result', {
            requestId: data.requestId,
            extension: data.extension,
            success: false,
            error: `Estensione ${data.extension} non attiva nel softphone corrente (attiva: ${currentExt})`,
            reason: 'extension_mismatch'
          });
        }
        return;
      }

      try {
        setIsOpen(true);
        setIsMinimized(false);

        const sanitizedDestination = data.destination.replace(/[\s\-\(\)]/g, '');

        if (data.auto_dial !== false) {
          const callResult = await sipPhoneService.call(sanitizedDestination);
          if (socketEmit) {
            socketEmit('sip:dial_result', {
              requestId: data.requestId,
              extension: data.extension,
              success: callResult,
              message: callResult ? `Chiamata avviata verso ${sanitizedDestination}` : 'Errore nell\'avvio della chiamata'
            });
          }
        } else {
          setPendingDialNumber(sanitizedDestination);
          if (socketEmit) {
            socketEmit('sip:dial_result', {
              requestId: data.requestId,
              extension: data.extension,
              success: true,
              message: `Dial pad aperto con numero ${data.destination} pre-compilato`
            });
          }
        }
      } catch (err) {
        console.error('[SipPhoneContext] sip:dial_number error:', err);
        toast({
          title: 'Errore chiamata',
          description: getErrorMessage(err) || 'Errore nell\'avvio della chiamata dal softphone.',
          variant: 'destructive'
        });
        if (socketEmit) {
          socketEmit('sip:dial_result', {
            requestId: data.requestId,
            extension: data.extension,
            success: false,
            error: getErrorMessage(err) || 'Errore sconosciuto',
            reason: 'call_error'
          });
        }
      }
    };

    // Task #1336 — badge "supervisione attiva" sul softphone dell'agente.
    const handleBeingSupervised = (data: { mode: 'listen' | 'whisper' | 'barge'; supervisor_extension: string | null; target_extension: string }) => {
      if (!userExtension || data.target_extension !== userExtension) return;
      setSupervisedSession({
        mode: data.mode,
        supervisorExtension: data.supervisor_extension,
        startedAt: Date.now(),
      });
    };
    const handleSupervisionEnded = (data: { target_extension: string }) => {
      if (!userExtension || data.target_extension !== userExtension) return;
      setSupervisedSession(null);
    };

    socketOn('sip:extension:status_change', handleExtensionStatusChange);
    socketOn('sip:extensions:health', handleExtensionsHealth);
    socketOn('crm:customer_identified', handleCustomerIdentified);
    socketOn('sip:dial_number', handleSipDialNumber);
    socketOn('extension:being_supervised', handleBeingSupervised);
    socketOn('extension:supervision_ended', handleSupervisionEnded);

    return () => {
      socketOff('sip:extension:status_change', handleExtensionStatusChange);
      socketOff('sip:extensions:health', handleExtensionsHealth);
      socketOff('crm:customer_identified', handleCustomerIdentified);
      socketOff('sip:dial_number', handleSipDialNumber);
      socketOff('extension:being_supervised', handleBeingSupervised);
      socketOff('extension:supervision_ended', handleSupervisionEnded);
    };
  }, [socketIsConnected, socketVersion, socketOn, socketOff, socketEmit, userExtension, activeExtension, refreshFsStatus]);

  const connect = useCallback(async (): Promise<boolean> => {
    if (isConnectedRef.current) {
      console.log('[SipPhoneContext] Already connected, skipping');
      return true;
    }

    console.log('[SipPhoneContext] Starting SIP connection...');

    try {
      const config = await loadOwnSipPhoneConfig();
      if (!config) {
        console.warn('[SipPhoneContext] Nessun interno assegnato dal backend');
        return false;
      }

      console.log('[SipPhoneContext] SIP config:', { extension: config.extension, server: config.server, domain: config.domain });
      const result = await sipPhoneService.connect(config);
      isConnectedRef.current = result;
      console.log('[SipPhoneContext] SIP connect result:', result);
      return result;
    } catch {
      console.warn('[SipPhoneContext] Provisioning SIP non disponibile; nessun percorso alternativo eseguito');
      return false;
    }
  }, []);

  const disconnect = useCallback(async (): Promise<void> => {
    await sipPhoneService.disconnect();
    isConnectedRef.current = false;
    setActiveExtension(null);
    registrationPromiseRef.current = null;
  }, []);

  // UI Actions - Persistent phone
  const openPhone = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const closePhone = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const clearPendingDialNumber = useCallback(() => {
    setPendingDialNumber(null);
  }, []);

  const minimizePhone = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizePhone = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Idempotent registration - prevents duplicate registrations
  // Respects admin-switched extensions (doesn't auto-revert to userExtension)
  const ensureRegistered = useCallback(async (): Promise<boolean> => {
    // Already registered? Return immediately (don't revert admin switches)
    if (isConnectedRef.current && activeExtension) {
      console.log('[SipPhoneContext] Already registered with extension:', activeExtension);
      return true;
    }
    
    // Registration in progress? Reuse the promise
    if (registrationPromiseRef.current) {
      console.log('[SipPhoneContext] Registration already in progress, waiting...');
      return registrationPromiseRef.current;
    }
    
    // Only auto-register if no activeExtension (first time or after logout)
    if (!activeExtension) {
      const promise = connect().then(result => {
        if (result) {
          setActiveExtension(userExtension || null);
        }
        registrationPromiseRef.current = null;
        return result;
      });
      
      registrationPromiseRef.current = promise;
      return promise;
    }
    
    return true;
  }, [connect, activeExtension, userExtension]);

  // Switch extension (for admins)
  const switchExtension = useCallback(async (extension: string): Promise<boolean> => {
    if (!canControlAllExtensions) {
      console.warn('[SipPhoneContext] User not authorized to switch extensions');
      return false;
    }
    
    // Switching back to own extension? Use normal connect flow
    if (extension === userExtension) {
      console.log('[SipPhoneContext] Switching back to own extension:', userExtension);
      if (isConnectedRef.current) {
        await disconnect();
      }
      const result = await connect();
      if (result) {
        setActiveExtension(null);
        console.log('[SipPhoneContext] Switched back to own extension successfully');
      }
      return result;
    }
    
    // Same extension? No need to switch
    const currentSipExt = sipPhoneService.getCurrentExtension();
    if (activeExtension === extension && isConnectedRef.current && currentSipExt === extension) {
      console.log('[SipPhoneContext] Already registered with this extension');
      return true;
    }
    
    console.log('[SipPhoneContext] Switching from', activeExtension || userExtension, 'to', extension);
    
    // Disconnect current registration
    if (isConnectedRef.current) {
      await disconnect();
    }
    
    try {
      // First get extension ID and display_name via exact-number lookup: a
      // paginated list filtered client-side missed numbers beyond the page.
      let extData: SipExtensionReadDto | null = null;
      try {
        const extensionResponse = await apiClient.getExtensionByNumber(extension);
        extData = extensionResponse?.success && extensionResponse.data ? extensionResponse.data : null;
      } catch (lookupErr) {
        console.error('[SipPhoneContext] Target extension lookup failed:', extension, lookupErr);
        return false;
      }
      
      if (!extData) {
        console.error('[SipPhoneContext] Target extension not found:', extension);
        return false;
      }

      // Fetch real SIP credentials using the dedicated endpoint. Shape estesa SoT.
      let sipPassword: string | null = null;
      let sipDomain = '';
      let wssUrl = '';
      try {
        const credResponse = await apiClient.get<SipExtensionProvisioningDto>(`/voip/sip-extensions/${extData.id}/sip-credentials`);
        if (credResponse?.success && credResponse?.data) {
          const credData = sipExtensionProvisioningSchema.parse(credResponse.data);
          sipPassword = credData.password ?? null;
          sipDomain = credData.sip_domain || '';
          wssUrl = credData.wss_url;
        }
      } catch (credErr) {
        console.error('[SipPhoneContext] /sip-credentials failed:', credErr);
        return false;
      }

      if (!wssUrl || !sipDomain || !sipPassword) {
        console.error('[SipPhoneContext] Switch: backend non ha restituito credenziali complete (wss_url/sip_domain/password)');
        return false;
      }

      const config: SipPhoneConfig = {
        server: wssUrl,
        extension: extension,
        password: sipPassword,
        domain: sipDomain,
        displayName: extData.display_name || extension
      };
      
      console.log('[SipPhoneContext] Switch config:', { extension: config.extension, server: config.server, domain: config.domain });
      const result = await sipPhoneService.connect(config);
      isConnectedRef.current = result;
      
      if (result) {
        setActiveExtension(extension);
      } else {
        console.error('[SipPhoneContext] SIP registration failed for extension:', extension);
      }
      
      return result;
    } catch (error) {
      console.error('[SipPhoneContext] Switch extension failed:', error);
      return false;
    }
  }, [canControlAllExtensions, activeExtension, userExtension, disconnect, connect]);

  // Auto-register on login
  useEffect(() => {
    if (isAuthenticated && userExtension && !autoRegisterDoneRef.current) {
      console.log('[SipPhoneContext] Auto-registering on login for extension:', userExtension);
      autoRegisterDoneRef.current = true;
      ensureRegistered().then(result => {
        console.log('[SipPhoneContext] Auto-registration result:', result);
      });
    }
    
    // Reset when user logs out
    if (!isAuthenticated) {
      autoRegisterDoneRef.current = false;
    }
  }, [isAuthenticated, userExtension, ensureRegistered]);

  const call = useCallback(async (destination: string): Promise<boolean> => {
    if (activeExtension && activeExtension !== userExtension) {
      const currentSipExt = sipPhoneService.getCurrentExtension();
      if (!isConnectedRef.current || currentSipExt !== activeExtension) {
        console.log('[SipPhoneContext] Re-switching to active extension:', activeExtension, '(SIP registered as:', currentSipExt, ')');
        const switched = await switchExtension(activeExtension);
        if (!switched) return false;
      }
    } else if (!isConnectedRef.current) {
      const connected = await connect();
      if (!connected) return false;
    }
    return sipPhoneService.call(destination);
  }, [connect, activeExtension, userExtension, switchExtension]);

  const answer = useCallback(async (): Promise<boolean> => {
    return sipPhoneService.answer();
  }, []);

  const hangup = useCallback(async (): Promise<boolean> => {
    return sipPhoneService.hangup();
  }, []);

  const hold = useCallback(async (): Promise<boolean> => {
    return sipPhoneService.hold();
  }, []);

  const unhold = useCallback(async (): Promise<boolean> => {
    return sipPhoneService.unhold();
  }, []);

  const mute = useCallback((): boolean => {
    return sipPhoneService.mute();
  }, []);

  const unmute = useCallback((): boolean => {
    return sipPhoneService.unmute();
  }, []);

  const transfer = useCallback(async (destination: string): Promise<boolean> => {
    return sipPhoneService.transfer(destination);
  }, []);

  const sendDTMF = useCallback((tone: string): boolean => {
    return sipPhoneService.sendDTMF(tone);
  }, []);

  const toggleHold = useCallback(async (): Promise<boolean> => {
    if (callInfo?.isHeld) {
      return unhold();
    } else {
      return hold();
    }
  }, [callInfo, hold, unhold]);

  const toggleMute = useCallback((): boolean => {
    if (callInfo?.isMuted) {
      return unmute();
    } else {
      return mute();
    }
  }, [callInfo, mute, unmute]);

  // Clear recognized customer when call ends
  useEffect(() => {
    if (state !== 'incoming' && state !== 'connected' && state !== 'ringing') {
      setRecognizedCustomer(null);
    }
  }, [state]);

  // Task #1336 — auto-clear badge a fine chiamata.
  useEffect(() => {
    if (state === 'idle' || state === 'registered') {
      setSupervisedSession(null);
    }
  }, [state]);

  const value: SipPhoneContextType = {
    state,
    callInfo,
    supervisedSession,
    isRegistered: state === 'registered' || state === 'connected' || state === 'calling' || state === 'ringing' || state === 'incoming' || state === 'on_hold',
    isInCall: state === 'connected' || state === 'calling' || state === 'ringing' || state === 'incoming' || state === 'on_hold',
    fsIsRegistered,
    fsConnectionType,
    // CRM Customer Recognition
    recognizedCustomer,
    // UI State
    isOpen,
    isMinimized,
    activeExtension,
    // Pending dial number
    pendingDialNumber,
    clearPendingDialNumber,
    // Permissions
    canControlAllExtensions,
    // Actions
    connect,
    disconnect,
    ensureRegistered,
    switchExtension,
    openPhone,
    closePhone,
    minimizePhone,
    maximizePhone,
    call,
    answer,
    hangup,
    hold,
    unhold,
    mute,
    unmute,
    transfer,
    sendDTMF,
    toggleHold,
    toggleMute,
    refreshFsStatus
  };

  return (
    <SipPhoneContext.Provider value={value}>
      {children}
    </SipPhoneContext.Provider>
  );
}

export function useSipPhone(): SipPhoneContextType {
  const context = useContext(SipPhoneContext);
  if (!context) {
    throw new Error('useSipPhone must be used within SipPhoneProvider');
  }
  return context;
}
